/**
 * Labstep
 *
 * @module state/store
 * @desc Set up redux store
 */

import { organizationAnalyticsApi } from 'labstep-web/rtk/services/organization-metric';
import { searchApi } from 'labstep-web/rtk/services/search';
import { configService } from 'labstep-web/services/config.service';
import { SensitiveService } from 'labstep-web/services/sensitive.service';
import epics from 'labstep-web/state/epics';
import reducers from 'labstep-web/state/reducers';
import LogRocket from 'logrocket';
import {
  applyMiddleware,
  combineReducers,
  compose,
  createStore,
} from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension';
import { createLogger } from 'redux-logger';
import { createEpicMiddleware } from 'redux-observable';
import { autoRehydrate, persistStore } from 'redux-persist';
import reduxThunk from 'redux-thunk';
import { entityCursorMiddleware } from './middleware/entity-cursor';
import { entityIdentifierResolver } from './middleware/entity-identifier-resolver';
import { entityPageMiddleware } from './middleware/entity-page';
import { fileDownloadGroupId } from './middleware/file-download-group-id';
import { optimistic } from './middleware/optimistic';
import { timeoutLogout } from './middleware/timeout-logout';

const { debug } = configService;

const epicMiddleware = createEpicMiddleware();

const middlewares: any[] = [
  reduxThunk,
  entityIdentifierResolver,
  entityCursorMiddleware, // Handle cursor-based pagination actions
  entityPageMiddleware, // Handle page pagination actions
  fileDownloadGroupId,
  timeoutLogout, // Keep timeoutLogout above epicMiddleware
  optimistic,
  epicMiddleware,
  organizationAnalyticsApi.middleware,
  searchApi.middleware,
];

// Append redux logger if in debug mode
if (
  debug &&
  configService.reactReduxLogger &&
  configService.nodeEnv !== 'test'
) {
  const reduxLogger = createLogger({
    collapsed: true,
  });
  middlewares.push(reduxLogger);
}

middlewares.push(
  LogRocket.reduxMiddleware({
    actionSanitizer(action) {
      return SensitiveService.purifyReduxAction(action);
    },
    stateSanitizer(state) {
      return SensitiveService.purifyReduxAction(state);
    },
  }),
);

let enhancers = compose(
  applyMiddleware(...middlewares),
  autoRehydrate(),
);

// // Chrome Dev Tools
if (debug) {
  enhancers = composeWithDevTools(enhancers);
}

const store = createStore(
  combineReducers(reducers),
  undefined,
  enhancers,
);

epicMiddleware.run(epics);

// if (module?.hot) {
//   module.hot.accept('./reducers', () => {
//     store.replaceReducer(reducers as any);
//   });
// }

// Not using persist for testing otherwise it breaks
if (configService.nodeEnv !== 'test') {
  persistStore(store, {
    whitelist: [
      'authenticatedUser',
      'activeGroupId',
      'ui_persistent',
      'thumbnail',
      'client',
      'user_settings',
    ],
  });
}

if ((window as any).Cypress) {
  (window as any).store = store;
}

export default store;
